import React, { useState, useRef, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { UploadIcon, FileIcon, XIcon } from './Icons';
import { normalizeFileName } from './utils/fileNameEncoding';

const FileUpload = React.forwardRef(({
  className,
  multiple = false,
  accept,
  maxSize = 10 * 1024 * 1024,
  value,
  onChange,
  name,
  disabled = false,
  readOnly = false,
  placeholder = "Drop files here or click to browse",
  showPreview = true,
  maxFiles = 5,
  onError,
  // New props for FormFileUpload compatibility
  onFilesChange,
  files: filesProp,
  onRemoveFile,
  showExistingFiles = false,
  existingFiles = [],
  loadingExistingFiles = false,
  ...props
}, ref) => {
  const { theme } = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const isDisabled = disabled || readOnly;

  // Use files prop if provided, otherwise use value
  const displayFiles = filesProp || (multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []));

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFiles = useCallback((files) => {
    console.log('🔍 Base FileUpload handleFiles called with:', files);
    const fileArray = Array.from(files);
    
    if (multiple && fileArray.length > maxFiles) {
      onError?.(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const oversizedFiles = fileArray.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      onError?.(`File(s) too large. Maximum size is ${formatFileSize(maxSize)}`);
      return;
    }

    // Use onFilesChange if provided (for FormFileUpload), otherwise use onChange
    if (onFilesChange) {
      onFilesChange(files);
    } else if (onChange) {
      const event = {
        target: {
          name,
          value: multiple ? fileArray : fileArray[0],
          type: 'file',
          files: fileArray
        }
      };
      console.log('🔍 Base FileUpload calling onChange with:', event);
      onChange(event);
    }
  }, [multiple, maxFiles, maxSize, onChange, onFilesChange, name, onError]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (isDisabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles, isDisabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!isDisabled) {
      setIsDragOver(true);
    }
  }, [isDisabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (!isDisabled) {
      setIsDragOver(false);
    }
  }, [isDisabled]);

  const handleClick = () => {
    if (!isDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const removeFile = (index) => {
    if (!multiple) {
      if (onChange) {
        onChange({
          target: {
            name,
            value: null,
            type: 'file'
          }
        });
      }
    } else {
      const currentFiles = Array.isArray(value) ? value : [];
      const newFiles = currentFiles.filter((_, i) => i !== index);
      if (onChange) {
        onChange({
          target: {
            name,
            value: newFiles,
            type: 'file'
          }
        });
      }
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div
        className={cn(
          "relative w-full min-h-[120px] rounded-md border-2 border-dashed transition-all duration-200",
          isDragOver ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-gray-400",
          theme.colors.surface,
          !isDisabled && 'cursor-pointer'
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={isDisabled}
          {...props}
        />
        
        <div className="flex flex-col items-center justify-center p-6 text-center">
          <UploadIcon className="w-12 h-12 text-gray-400 mb-4" />
          
          <p className="text-sm text-gray-600 mb-2">
            {placeholder}
          </p>
          
          <p className="text-xs text-gray-500">
            {accept && `Accepted: ${accept}`}
            {accept && maxSize && ' • '}
            {maxSize && `Max size: ${formatFileSize(maxSize)}`}
            {multiple && maxFiles && ` • Max files: ${maxFiles}`}
          </p>
        </div>
      </div>

      {displayFiles.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium">
            {multiple ? `Selected Files (${displayFiles.length})` : 'Selected File'}
          </h4>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {displayFiles.map((file, index) => {
              // Handle both File objects and file attachment objects
              const fileName = normalizeFileName(file.name || file.fileName || '');
              const fileSize = file.size || file.fileSize;
              const isExisting = file.isExisting || file.id !== undefined; // Check both flags
              const fileId = file.id;
              
              return (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                  <FileIcon className="w-6 h-6 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{fileName}</p>
                    <p className="text-xs text-gray-500">
                      {fileSize && formatFileSize(fileSize)}
                      {isExisting && <span className="ml-2 text-blue-600">(Existing)</span>}
                    </p>
                    {isExisting && fileId && (
                      <a 
                        href={API_ENDPOINTS.fileAttachment.download(fileId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 underline mt-1 inline-block"
                      >
                        Download
                      </a>
                    )}
                  </div>
                  {onRemoveFile && (
                    <button
                      type="button"
                      onClick={() => onRemoveFile(index)}
                      className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center transition-colors",
                        isExisting 
                          ? "bg-red-500 text-white hover:bg-red-600" 
                          : "bg-red-500 text-white hover:bg-red-600"
                      )}
                      title={isExisting ? "Delete existing file" : "Remove file"}
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {showExistingFiles && loadingExistingFiles && (
        <div className="text-sm text-gray-500">
          Loading existing files...
        </div>
      )}
    </div>
  );
});

const SingleFileUpload = React.forwardRef((props, ref) => {
  return <FileUpload ref={ref} multiple={false} {...props} />;
});

const MultipleFileUpload = React.forwardRef((props, ref) => {
  return <FileUpload ref={ref} multiple={true} {...props} />;
});

FileUpload.displayName = "FileUpload";
SingleFileUpload.displayName = "SingleFileUpload";
MultipleFileUpload.displayName = "MultipleFileUpload";

export { FileUpload, SingleFileUpload, MultipleFileUpload };
